import {DragComponent} from '@fantasmagames/core/src/interaction/DragComponent';
import {IInputController, IInputListener} from '@fantasmagames/core/src/interaction/DraggingComponent';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';

// TODO: Decouple Scrollbar from both PIXI and graphics. Make abstract class and implement from that.

export class ScrollbarConstants {
	public static SCROLLBAR_WIDTH: number = 12;
	public static SCROLLBAR_WIDTH_MOBILE: number = 20;
}

export interface I2dLimits {
	maxX: number;
	maxY: number;
	minX: number;
	minY: number;
}

export abstract class AbstractScrollbar {
	protected _minY: number;
	protected _maxY: number;

	protected _minX: number;
	protected _maxX: number;

	protected _barObject: any;
	protected _barBackground: any;

	public moveTo(newPosition: number): void {
		this._barObject.moveTo(newPosition);
	}
}

export class Scrollbar extends PIXI.Sprite implements IInputController, IInputListener {
	public _listeningComponents: IInputListener[];
	public _controllingComponents: IInputController[];

	protected _barWidth: number;

	protected _parentContainer: PIXI.Container;

	protected _visibleHeight: number;
	protected _containerTotalHeight: number;
	protected _totalBarHeight: number;

	protected _barObject: BarObject;
	protected _background: PIXI.Sprite | null;

	constructor(
		width: number,
		totalBarHeight: number,
		visibleHeight: number,
		containerTotalHeight: number,
		bgColor: number = 0x222222,
		barColor: number = 0xcccccc,
	) {
		super();

		this._barWidth = width;
		this._totalBarHeight = totalBarHeight;
		this._visibleHeight = visibleHeight;
		this._containerTotalHeight = containerTotalHeight;

		this.initialize(width, totalBarHeight, bgColor, barColor);
	}

	public moveBarObject(newPosition: number): void {
		this._barObject.moveTo(newPosition);
	}

	public onDragUpdateEvent(data: any): void {
		this.moveBarObject(1 - data.fractY);
	}

	public setupInput(): void {
		this.barObject.interactive = this.barObject.buttonMode = true;
	}

	public updateListeners(data: any): void {
		for (let index = 0; index < this._listeningComponents.length; index++) {
			const component = this._listeningComponents[index];
			component.updateFromController(data);
		}
	}

	public bindListener(object: any): void {
		this._listeningComponents.push(object);
	}

	public unbindListener(object: any): void {
		if (this._listeningComponents.indexOf(object) > -1) {
			this._listeningComponents.splice(this._listeningComponents.indexOf(object), 1);
		}
	}

	public updateFromController(data: any): void {
		this.onDragUpdateEvent(data);
	}

	protected initialize(width: number, height: number, bgColor: number, barColor: number): void {
		this.createBg(width, height, bgColor);
		this.createBarObject(width, barColor);
	}

	protected createBg(width: number, height: number, color: number): void {
		const bgGFX = new PIXI.Graphics();

		bgGFX.beginFill(color, 0.5);
		bgGFX.drawRect(0, 0, width, height);
		bgGFX.endFill();

		this._background = new PIXI.Sprite(bgGFX.generateCanvasTexture());
		this.addChild(this._background);

		bgGFX.destroy();
	}

	protected createBarObject(width: number, color: number): void {
		const bgGFX = new PIXI.Graphics();
		const height: number = (this._visibleHeight / this._containerTotalHeight) * this._totalBarHeight;

		bgGFX.beginFill(color, 0.5);
		bgGFX.drawRect(0, 0, width, height);
		bgGFX.endFill();

		this._barObject = new BarObject(bgGFX.generateCanvasTexture());
		this._barObject.setLimits(0, this._totalBarHeight - height);
		this._barObject.moveTo(0);
		this.addChild(this._barObject);

		bgGFX.destroy();
	}

	public get barObject(): BarObject {
		return this._barObject;
	}
}

/**
 * An interactive scrollbar that takes input from the user.
 *
 * @export
 * @class InteractiveScrollbar
 * @extends {Scrollbar}
 */
export class InteractiveScrollbar extends Scrollbar {
	protected _dragComponent: DragComponent;

	public setupInteraction(
		updateManager: UpdateManager,
		targetContainer: PIXI.Container,
		isMobile: boolean,
		targetLimits: I2dLimits,
	): void {
		this._dragComponent = new DragComponent(updateManager, targetContainer, this._barObject, isMobile);

		this._dragComponent.maxY = targetLimits.maxY;
		this._dragComponent.minY = targetLimits.minY;
		this._dragComponent.maxX = targetContainer.x;
		this._dragComponent.minX = targetContainer.x;

		this._dragComponent.interactionMaxY = this._barObject.maxY;
		this._dragComponent.interactionMinY = this._barObject.minY;
		this._dragComponent.interactionMaxX = this._barObject.x;
		this._dragComponent.interactionMinX = this._barObject.x;

		this._dragComponent.canDrag = true;
	}

	public canDrag(bool: boolean): void {
		this._dragComponent.canDrag = bool;
	}

	protected createBarObject(width: number, color: number): void {
		const bgGFX = new PIXI.Graphics();
		const height: number = (this._visibleHeight / this._containerTotalHeight) * this._totalBarHeight;

		bgGFX.beginFill(color, 0.5);
		bgGFX.drawRect(0, 0, width, height);
		bgGFX.endFill();

		this._barObject = new InteractiveBarObject(bgGFX.generateCanvasTexture());
		this._barObject.setLimits(0, this._totalBarHeight - height);
		this._barObject.moveTo(0);
		this.addChild(this._barObject);

		bgGFX.destroy();
	}

	protected onTouch(): void {
		// this._dragComponent.
	}

	protected addEventListeners(): void {}

	protected clearEventListeners(): void {}
}

/**
 * Base Bar Object.
 *
 * @class BarObject
 * @extends {PIXI.Sprite}
 */
class BarObject extends PIXI.Sprite {
	protected _minY: number = 0;
	protected _maxY: number = 0;

	constructor(texture: PIXI.Texture) {
		super(texture);
	}

	/**
	 *
	 * @param {number} newY Should be provided in a 0-1 range from max to min.
	 * @memberof BarObject
	 */
	public moveTo(newY: number): void {
		newY = Math.abs(this._minY - this._maxY) * newY;
		newY = newY > this._maxY ? this._maxY : newY < this._minY ? this._minY : newY;

		this.y = newY;
	}

	public setLimits(minY: number, maxY: number): void {
		this._minY = minY ? minY : 0;
		this._maxY = maxY ? maxY : 0;
	}

	public get minY(): number {
		return this._minY;
	}

	public get maxY(): number {
		return this._maxY;
	}
}

/**
 * Interactive bar object, takes input from user.
 *
 * @class InteractiveBarObject
 * @extends {BarObject}
 */
class InteractiveBarObject extends BarObject {
	constructor(texture: PIXI.Texture) {
		super(texture);

		this.interactive = this.buttonMode = true;
	}

	public enable(): void {
		this.interactive = this.buttonMode = true;
	}

	public disable(): void {
		this.interactive = this.buttonMode = false;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/popupmenus/Scrollbar.ts